import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPromoter, getPromoters } from "../../api/promotersApi";
import PageLoader from "../../components/pageLoader";
import ToastContainer from "../../components/toastContainer";
import Toast from "../../components/toast";
import PromoterFormModal from './PromoterFormModal';

const PromotersPage = () => {
  const [promoters, setPromoters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const navigate = useNavigate();

  const [showAddModal, setShowAddModal] = useState(false);
  const [creating, setCreating] = useState(false);

  const fetchPromoters = async () => {
    try {
      setLoading(true);
      const data = await getPromoters();

      const list = Array.isArray(data) ? data : data.results || [];
      setPromoters(list);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromoters();
  }, []);

  const formatAddress = (address) => {
    if (!address) return "-";

    const parts = [
      address.address_1,
      address.address_2,
      address.address_3,
      address.city,
      address.county,
      address.country,
      address.postcode,
    ];

    const filtered = parts.filter((p) => p && p.trim() !== "");

    return filtered.length ? filtered.join(", ") : "-";
  };

  const formatAirports = (airports) => {
    if (!Array.isArray(airports) || airports.length === 0) {
      return "-";
    }

    return airports
      .map((a) => a.name)
      .filter(Boolean)
      .join(", ");
  };

  const handleCreatePromoter = async (data) => {
    try {
      //setLoading(true);
      setCreating(true);

      const payload = {
        name: data.name,
        company_name: data.company_name,
        contact_name: data.contact_name,
        contact_email: data.contact_email,
        contact_phone: data.contact_phone,

        company_id: data.company_id,
        company_vat_no: data.company_vat_no,
        company_reg_no: data.company_reg_no,

        closest_airports: data.closestAirports.map((name) => ({ name })),

        address: {
          address_1: data.addressLine1,
          address_2: data.addressLine2,
          address_3: data.addressLine3,
          city: data.city,
          country: data.country,
          postcode: data.postcode,
        },

        venue: {
          name: data.venue_name,
          address: {
            address_1: data.venue_addressLine1,
            address_2: data.venue_addressLine2,
            address_3: data.venue_addressLine3,
            city: data.venue_city,
            county: data.venue_county,
            country: data.venue_country,
            postcode: data.venue_postcode,
          },
        },
      };

      await createPromoter(payload);

      setToast({
        type: "success",
        message: "Promoter created successfully",
      });

      setShowAddModal(false);
      fetchPromoters(); // refresh list
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail,
      });
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Promoters</h1>
          <p className="text-sm text-slate-400">Manage promoters</p>
        </div>

        <button
          onClick={() => {
            setShowAddModal(true);
          }}
          className="px-4 py-2 rounded-lg bg-pink-600 hover:bg-pink-700 text-white text-sm font-medium transition"
        >
          + Add Promoter
        </button>
      </div>

      <div className="bg-slate-900/60 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-white/10 text-slate-400">
            <tr>
              <th className="px-6 py-3 text-left">Name</th>
              <th className="px-6 py-3 text-left">Company</th>
              <th className="px-6 py-3 text-left">Address</th>
              <th className="px-6 py-3 text-left">Contact</th>
              <th className="px-6 py-3 text-left">Closest Airport</th>
              <th className="px-6 py-3 text-left">Last Booking</th>
              <th className="px-6 py-3 text-right">Total Bookings</th>
              <th className="px-6 py-3 text-right">Total Revenue</th>
            </tr>
          </thead>

          <tbody>
            {promoters.map((p) => (
              <tr
                key={p.id}
                onClick={() => navigate(`/promoters/${p.id}`)}
                className="border-b border-white/5 hover:bg-slate-800/40 transition cursor-pointer"
              >
                <td className="px-6 py-4 text-white font-medium">{p.name}</td>
                <td className="px-6 py-4 text-slate-300">
                  {p.company_name || "-"}
                </td>
                <td className="px-6 py-4 text-slate-300">
                  {formatAddress(p.address)}
                </td>
                <td className="px-6 py-4 text-slate-300">
                  {p.contact_phone || "-"}
                </td>
                <td className="px-6 py-4 text-slate-300">
                  {formatAirports(p.closest_airports)}
                </td>
                <td className="px-6 py-4 text-slate-300">
                  {p.last_booking || "-"}
                </td>
                <td className="px-6 py-4 text-right text-slate-300">
                  {p.total_bookings ?? 0}
                </td>
                <td className="px-6 py-4 text-right text-green-400 font-medium">
                  {p.total_revenue ?? "-"}
                </td>
              </tr>
            ))}

            {!promoters.length && (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-10 text-center text-slate-400"
                >
                  No promoters found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <PromoterFormModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleCreatePromoter}
        loading={false}
      />

      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

export default PromotersPage;
